/**
 * Year-focused helpers built on top of dateCalculator + bengaliDate utilities.
 * Hijri is approximated arithmetically; for full date precision use the
 * tabular converter in dateCalculator.ts.
 */
import { addDays, addYears, differenceInCalendarDays, differenceInCalendarMonths, format } from "date-fns";
import { gregorianToBangla } from "@/lib/bengaliDate";
import { gregorianToHijri } from "@/lib/calculators/dateCalculator";

export interface YearsBetween {
  years: number;          // full years
  months: number;         // remainder months after years
  days: number;           // remainder days after months
  totalYearsDecimal: number;
  totalMonths: number;
  totalDays: number;
}

export function yearsBetween(start: Date, end: Date): YearsBetween {
  const a = start <= end ? start : end;
  const b = start <= end ? end : start;
  let years = b.getFullYear() - a.getFullYear();
  let probe = addYears(a, years);
  if (probe > b) { years--; probe = addYears(a, years); }
  const months = differenceInCalendarMonths(b, probe);
  const probe2 = new Date(probe);
  probe2.setMonth(probe2.getMonth() + months);
  const days = differenceInCalendarDays(b, probe2);
  const totalDays = differenceInCalendarDays(b, a);
  return {
    years,
    months,
    days,
    totalYearsDecimal: totalDays / 365.25,
    totalMonths: years * 12 + months,
    totalDays,
  };
}

export interface BirthYearRange {
  earliest: Date;  // earliest possible birth date
  latest: Date;    // latest possible birth date
  earliestYear: number;
  latestYear: number;
}

/**
 * Given an age and an "as of" date, returns the inclusive range of birth dates.
 * Someone aged N as of date D was born in (D − (N+1)years + 1day) … (D − N years).
 */
export function birthYearFromAge(age: number, asOf: Date): BirthYearRange {
  const latest = addYears(asOf, -age);
  const earliest = addDays(addYears(asOf, -(age + 1)), 1);
  return {
    earliest,
    latest,
    earliestYear: earliest.getFullYear(),
    latestYear: latest.getFullYear(),
  };
}

/** Bengali year(s) covered by a given Gregorian year (always two: pre/post April 14). */
export function banglaYearsForGregorian(gy: number): { early: number; late: number } {
  const early = gregorianToBangla(new Date(gy, 0, 1)).year;     // before Apr 14
  const late = gregorianToBangla(new Date(gy, 11, 31)).year;     // after Apr 14
  return { early, late };
}

/** Approximate Hijri year for a given Gregorian year. */
export function approxHijriYear(gy: number): number {
  return gregorianToHijri(new Date(gy, 6, 1)).year; // mid-year sample
}

export function isoDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}
